import { ThemeOptions } from '@mui/material';
import { mainThemeConfig } from '@qrios/frontend-library';

import { merriweather, roboto } from '../../assets/fonts';

const { customComponents, ...commonConfig } = mainThemeConfig;

export const PORTAL_BREAKPOINTS = {
  xs: 0,
  sm: 720,
  md: 1025,
  lg: 1200,
  xl: 1536
} as const;

/**
 * Corner radius scale of the portal.
 *
 * Ideally `shape.borderRadius` alone would be enough, but @qrios/frontend-library bundles its own
 * copy of @mui/material and rebuilds a theme from `mainThemeConfig` around every component, which
 * resets the shape to the MUI default. Component `styleOverrides` survive that merge, so every
 * radius the portal cares about is declared explicitly below. Use these tokens instead of raw
 * pixels when rounding anything new, so that corners stay consistent across the portal.
 */
export const RADIUS = {
  /** controls: form fields, buttons, alerts, tooltips, menu items */
  sm: 8,
  /** small standalone boxes: sidebar tiles, small call-to-actions */
  md: 12,
  /** large surfaces: menus, popovers, grouped content, tables */
  lg: 16,
  /** cards and dialogs */
  xl: 20,
  /** fully rounded: chips, pills */
  pill: 999
} as const;

export const commonThemeConfig: ThemeOptions = {
  ...commonConfig,
  shape: {
    borderRadius: RADIUS.sm
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
      html {
        height: 100%;
      };
      body {
        height: 100%;
        overflow-X: hidden;
        '@media screen and ( max-height: 651px ) {
          overflow: 'auto'
         }
       };
       @font-face {
          ${roboto}
       }
       @font-face {
          ${merriweather}
       }
       #root {
          height: 100%;
       }
      `
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: RADIUS.lg
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS.xl
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: RADIUS.xl,
          // below the `sm` breakpoint dialogs are fullscreen, corners would only look like a glitch
          [`@media (max-width: ${PORTAL_BREAKPOINTS.sm - 0.05}px)`]: {
            borderRadius: 0
          }
        }
      }
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS.lg,
          // without the clip the header and the rows spill over the rounded corners
          overflow: 'hidden'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS.sm
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS.sm
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS.sm
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        popper: {
          borderRadius: RADIUS.sm
        },
        tooltip: {
          borderRadius: RADIUS.sm
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS.sm
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS.pill
        }
      }
    }
  }
};

export const portalThemeConfig: ThemeOptions = {
  ...commonThemeConfig,
  customComponents: {
    ...customComponents,
    card: {
      ...customComponents.card,
      borderRadius: `${RADIUS.xl}px`
    },
    usageSummaryMonthSelector: {
      height: '72px'
    },
    profileCreation: {
      container: {
        width: '100%',
        maxWidth: '600px'
      },
      warningBox: {
        width: '100%',
        maxWidth: '528px'
      }
    }
  },
  breakpoints: {
    values: PORTAL_BREAKPOINTS
  }
};
